import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MobileLayout } from '../../components/MobileLayout';
import { Tabs } from '../../components/ui/Tabs';
import { Clock, MapPin, User, ChevronRight, UserPlus, X, Check, Home } from 'lucide-react';
import { Card, CardContent, CardFooter } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { useScreenInit } from '../../useScreenInit.js';
import { usePlatform, initials, Job } from '../../context/PlatformContext';
const statusMap: Record<Job['status'], {
  label: string;
  cls: string;
}> = {
  da_assegnare: {
    label: 'Da assegnare',
    cls: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  assegnata: {
    label: 'Assegnata',
    cls: 'bg-muted text-muted-foreground border-border'
  },
  in_corso: {
    label: '● In corso',
    cls: 'bg-coral/5 text-coral border-coral/20'
  },
  completata: {
    label: 'Completata',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  }
};
export function CmIncarichiPage() {
  const screenInit = useScreenInit() as {
    tab?: string;
  };
  const navigate = useNavigate();
  const {
    jobs,
    cleaners,
    assignCleaner,
    activeLoad
  } = usePlatform();
  const [tab, setTab] = useState(screenInit?.tab ?? 'attivi');
  const [assignFor, setAssignFor] = useState<Job | null>(null);
  const visible = jobs.filter(j => tab === 'attivi' ? j.status !== 'completata' : j.status === 'completata');
  // Cleaners that cover the job's zone (fallback: all) — never offer 'off' ones.
  const eligibleCleaners = useMemo(() => {
    if (!assignFor) return [];
    const inZone = cleaners.filter(c => c.availability !== 'off' && c.zones.some(z => z.toLowerCase() === assignFor.zone.toLowerCase()));
    const base = inZone.length > 0 ? inZone : cleaners.filter(c => c.availability !== 'off');
    return base;
  }, [assignFor, cleaners]);
  const openJob = (j: Job) => {
    navigate(`/cm/clienti/appartamenti/${j.apartmentId}`);
  };
  const handleAssign = (cleanerId: string) => {
    if (!assignFor) return;
    assignCleaner(assignFor.id, cleanerId);
    setAssignFor(null);
  };
  return <MobileLayout data-id="element-2383">
      <div className="mb-5" data-id="element-2384">
        <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-2385">Incarichi</h1>
      </div>

      <Tabs className="mb-6" value={tab} onValueChange={setTab} items={[{
      id: 'attivi',
      label: 'Attivi'
    }, {
      id: 'completati',
      label: 'Completati'
    }]} data-id="element-2386" />

      {visible.length > 0 ? <div className="space-y-3" data-id="element-2387">
          {visible.map(j => <Card key={j.id} className="hover:border-coral/40 hover:shadow-sm transition-all" data-id="element-2388">
              <CardContent className="flex items-center justify-between gap-3 cursor-pointer" onClick={() => openJob(j)} data-id="element-2389">
                <div className="min-w-0" data-id="element-2390">
                  <div className="flex items-center gap-2 mb-1" data-id="element-2391">
                    <h3 className="text-sm font-semibold text-foreground truncate" data-id="element-2392">
                      {j.apartmentName}
                    </h3>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border flex-shrink-0 ${statusMap[j.status].cls}`} data-id="element-2393">
                      {statusMap[j.status].label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap" data-id="element-2394">
                    <span className="flex items-center gap-1" data-id="element-2395">
                      <Home size={12} data-id="element-2396" />
                      {j.type}
                    </span>
                    <span className="flex items-center gap-1" data-id="element-2397">
                      <MapPin size={12} data-id="element-2398" />
                      {j.zone}
                    </span>
                    <span className="flex items-center gap-1" data-id="element-2399">
                      <Clock size={12} data-id="element-2400" />
                      {j.date} · {j.time}
                    </span>
                    {j.cleanerName && <span className="flex items-center gap-1" data-id="element-2401">
                        <User size={12} data-id="element-2402" />
                        {j.cleanerName}
                      </span>}
                  </div>
                </div>
                <ChevronRight size={16} className="text-muted-foreground flex-shrink-0" data-id="element-2403" />
              </CardContent>
              {j.status === 'da_assegnare' && <CardFooter data-id="element-2404">
                  <span className="text-xs text-muted-foreground" data-id="element-2405">
                    Nessun cleaner assegnato
                  </span>
                  <Button size="sm" onClick={() => setAssignFor(j)} data-id="element-2406">
                    <UserPlus size={15} data-id="element-2407" />
                    Assegna cleaner
                  </Button>
                </CardFooter>}
            </Card>)}
        </div> : <div className="bg-white border border-border rounded-xl p-10 text-center" data-id="element-2408">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3" data-id="element-2409">
            <Clock size={20} className="text-muted-foreground" data-id="element-2410" />
          </div>
          <p className="text-sm font-medium text-foreground" data-id="element-2411">
            {tab === 'attivi' ? 'Nessun incarico attivo' : 'Nessun incarico completato'}
          </p>
          <p className="text-xs text-muted-foreground mt-1" data-id="element-2412">
            {tab === 'attivi' ? 'Gli incarichi generati dai contratti compariranno qui.' : 'Gli incarichi completati appariranno qui.'}
          </p>
        </div>}

      {/* Assign cleaner modal */}
      {assignFor && <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setAssignFor(null)} data-id="element-2413">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Assegna cleaner" data-id="element-2414">
            <div className="flex items-center justify-between p-4 border-b border-border" data-id="element-2415">
              <div className="min-w-0" data-id="element-2416">
                <h2 className="text-lg font-bold text-navy" data-id="element-2417">Assegna cleaner</h2>
                <p className="text-xs text-muted-foreground truncate" data-id="element-2418">
                  {assignFor.apartmentName} · {assignFor.zone} ·{' '}
                  {assignFor.date}
                </p>
              </div>
              <button onClick={() => setAssignFor(null)} aria-label="Chiudi" className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-2419">
                <X size={20} className="text-gray-500" data-id="element-2420" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto space-y-2" data-id="element-2421">
              {eligibleCleaners.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6" data-id="element-2422">
                  Nessun cleaner disponibile per questa zona.
                </p> : eligibleCleaners.map(c => <button key={c.id} type="button" onClick={() => handleAssign(c.id)} className="w-full flex items-center gap-3 p-3 rounded-xl border border-border hover:border-coral hover:bg-coral/5 transition-colors text-left" data-id="element-2423">
                    <span className="w-10 h-10 rounded-full bg-coral/10 flex items-center justify-center text-xs font-bold text-coral flex-shrink-0" data-id="element-2424">
                      {initials(c.name)}
                    </span>
                    <div className="flex-1 min-w-0" data-id="element-2425">
                      <div className="text-sm font-semibold text-navy truncate" data-id="element-2426">
                        {c.name}
                      </div>
                      <div className="text-xs text-muted-foreground truncate" data-id="element-2427">
                        {c.zones.join(', ')} · {activeLoad(c.id)} attivi
                      </div>
                    </div>
                    <Check size={18} className="text-coral flex-shrink-0" data-id="element-2428" />
                  </button>)}
            </div>
          </div>
        </div>}
    </MobileLayout>;
}